import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useFormFunnelTracking } from "../hooks/useFormFunnelTracking";
import { useCallback } from "react";
import Link from "next/link";
import { FunnelButton } from "../FunnelButton";
import { toRelativePath } from "../utils/urlUtils";
import { getUrls } from "@portal/app/_common/lib/urls";
import { useFormFunnelHeadingLevel } from "../FormFunnelHeadingContext";

export interface NoMatchResultProps {
  searchUrl?: string;
  sessionId?: string;
  similarPrograms?: {
    id: string;
    name: string;
    providerName: string;
    providerSlug: string;
    slug: string;
  }[];
}

export const NoMatchResult = ({
  searchUrl = "/explore",
  sessionId,
  similarPrograms = [],
}: NoMatchResultProps) => {
  const headingLevel = useFormFunnelHeadingLevel();
  const tracking = useFormFunnelTracking();
  const hasCloseMatches = similarPrograms.length > 0;

  const handleSearchClick = useCallback(() => {
    tracking.trackEvent("Form Funnel - No Match - Search Clicked", {
      destinationUrl: searchUrl,
      sessionId,
    });
  }, [tracking, searchUrl, sessionId]);

  const handleProgramClick = useCallback(
    (programId: string) => {
      tracking.trackEvent("Form Funnel - No Match - Similar Program Clicked", {
        programId,
        sessionId,
      });
    },
    [tracking, sessionId]
  );

  return (
    <Box
      data-testid="no-match-result"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 3,
        p: 4,
        textAlign: "center",
        maxWidth: 600,
        mx: "auto",
        border: "1px solid #eee",
        borderRadius: 2,
        boxShadow: 1,
      }}
    >
      <Typography variant="h4" component={headingLevel}>
        {hasCloseMatches ? "We found some close matches" : "No matching programs found"}
      </Typography>

      <Typography variant="body1" color="text.secondary">
        {hasCloseMatches
          ? "We couldn't find an exact match for your preferences, but we found some similar programs you might like."
          : "We couldn't find any programs matching your preferences in your area. Try browsing all programs to find options near you."}
      </Typography>

      {similarPrograms.length > 0 && (
        <Box sx={{ width: "100%", textAlign: "left", mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            You might be interested in:
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            {similarPrograms.map((program) => (
              <Box component="li" key={program.id} sx={{ mb: 1 }}>
                <Link
                  href={getUrls().ProviderProgramStorefront(program.providerSlug, program.slug)}
                  onClick={() => handleProgramClick(program.id)}
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <Typography
                    variant="body1"
                    color="primary"
                    sx={{ textDecoration: "underline", cursor: "pointer" }}
                  >
                    {program.providerName} - {program.name}
                  </Typography>
                </Link>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <Box
        sx={{
          mt: 2,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography variant="body2" sx={{ mb: 2 }}>
          {hasCloseMatches ? "Or browse all programs:" : "Browse all programs:"}
        </Typography>
        <Link href={toRelativePath(searchUrl)} passHref legacyBehavior>
          <FunnelButton
            component="a"
            onClick={handleSearchClick}
            data-testid="no-match-search-button"
          >
            View All Programs
          </FunnelButton>
        </Link>
      </Box>
    </Box>
  );
};
